import { withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from "react";
import { useTranslation, withTranslation } from 'react-i18next';
import { removeOrderAPI, sendOrderEmail } from '../../../api/medicalRecord';
import { AuditModal, CreateAudit, CreateAuditModal } from "../../../components/AuditTrail/AuditModal";
import { IconSVG } from '../../../components/Icons/Icons';
import { OrdersForm } from "../../../components/MedicalRecords/Forms/OrdersForm";
import { Orders } from '../../../components/MedicalRecords/Orders';
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import { ModalSendEmail } from '../../../components/Modals/ModalSendEmail';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { Success } from '../../../components/Notifications/Notifications';
import { ModalSpinner } from '../../../components/Utils/Loaders';
import { getMedicalRecordsSchema } from '../../../components/Validations/FormValidationSchema';
import { AuditTrail } from "../../Admin/AuditTrail";
import { useDrug } from "../Hooks/useDrugs";
import { useOrder, useOrders } from '../Hooks/useOrders';
import { ModalData } from '../Modals/ModalData';

export const OrdersContainer = (props) => {
    const [modal, setModal] = useState(null);
    const { entryStatus, entryId, medicalRecordNumber, showAddButton, expandedAll } = props;
    const [orders, query, totalSize, isLoading, handleTableChange, setReload] = useOrders(medicalRecordNumber, null);
    const [, , , , , print, recreate] = useOrder();
    const { t } = useTranslation();

    const handleAdd = () => {
        setModal(
            <ModalData
                isShowing={true}
                hide={handleClose}
                title={t(`medicalRecords.orders.title`)}
            >
                <OrdersCreateContainer {...props} hide={handleClose} onSubmit={handleSubmit} onCreated={handleCreatedOrder} />
            </ModalData>
        );
    }

    const handleEdit = (id) => {
        setModal(
            <ModalData
                isShowing={true}
                hide={handleClose}
                title={t(`medicalRecords.orders.title`)}
            >
                <OrderEditContainer
                    medicalRecordNumber={medicalRecordNumber}
                    entryId={entryId}
                    entryStatus={entryStatus}
                    id={id}
                    patient={props.patient}
                    onSubmit={handleSubmit}
                    onCancel={handleClose}
                    onCreated={handleCreatedOrder}
                />
            </ModalData>);
    }

    const handleCopy = async (_entryId2, id) => {
        setModal(
            <ModalData
                isShowing={true}
                hide={handleClose}
                title={t(`medicalRecords.orders.title`)}
            >
                <OrderCopyContainer
                    medicalRecordNumber={medicalRecordNumber}
                    entryId={entryId}
                    entryStatus={entryStatus}
                    id={id}
                    patient={props.patient}
                    onSubmit={handleSubmit}
                    onCancel={handleClose}
                    onCreated={handleCreatedOrder}
                />
            </ModalData>);
    }

    const handleSubmit = () => {
        setModal(null);
        setReload(true);
    }

    const handleClose = () => {
        setModal(null);
    }

    const handleAuditTrail = (entityId) => {
        setModal(
            <AuditModal
                onClose={() => { setModal(null); }}
                title={t("auditTrail.audit")}
            >
                <AuditTrail entity="OrdersItems" entityId={entityId} medicalRecordNumber={medicalRecordNumber} />
            </AuditModal>
        )
    }

    const handlePrint = async (entryId2, id) => {
        try {
            var response = await print(medicalRecordNumber, entryId2, id, null);

            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            HandleApiError(error);
            console.log(error);
        };
    }

    const handleRecreateExternal = async (entryId2, itemId) => {
        try {
            await recreate(medicalRecordNumber, entryId2, itemId);
            setReload(true);
        }
        catch (error) {
            HandleApiError(error);
            console.log(error);
        };
    }

    /// Manejo de envío de email
    const handleCreatedOrder = (createdOrder) => {
        // cierro el modal de Ordenes
        setModal(null);

        // Mostrar modal de envío de email
        setModal(
            <ModalSendEmail
                onClose={() => {
                    setModal(null);
                    setReload(true);
                }}
                onSend={async (emailData) => {
                    await sendOrderEmail(
                        medicalRecordNumber,
                        entryId,
                        createdOrder.id,
                        {
                            ...emailData,
                            externalPrescriptionUrl: createdOrder.externalPrescriptionURL
                        }
                    );
                    Success('email.sentSuccessfully');
                    setModal(null);
                    setReload(true);
                }}
                onSkip={() => {
                    setModal(null);
                    setReload(true);
                }}
                defaultEmail={
                    props.patient?.telecom?.email ||
                    props.patient?.email ||
                    ''
                }
                title={t('email.sendOrder')}
            />
        );
    };

    const handleSendEmail = (entryId2, id, externalOrderURL) => {
        setModal(
            <ModalSendEmail
                onClose={() => setModal(null)}
                onSend={async (emailData) => {
                    await sendOrderEmail(
                        medicalRecordNumber,
                        entryId2,
                        id,
                        {
                            ...emailData,
                            externalPrescriptionUrl: externalOrderURL
                        }
                    );
                    Success('email.sentSuccessfully');
                    setModal(null);
                }}
                onSkip={() => setModal(null)}
                defaultEmail={
                    props.patient?.telecom?.email ||
                    props.patient?.email ||
                    ''
                }
                title={t('email.sendOrder')}
            />
        );
    };

    //#region Delete item with audit
    const handleRemove = (id) => {
        setModal(
            <ModalConfirm
                onConfirm={() => confirmRemoveItem(id)}
                onCancel={() => setModal(null)}
                title={t("medicalRecordsNotifications.item_delete_title")}
                description={t("medicalRecordsNotifications.item_delete_description")}
                confirmStyle="danger"
            />);
    }
    const confirmRemoveItem = (id) => {
        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setModal(<CreateAuditModal
                onClose={() => setModal(null)}
                onSave={(reason, comments) => _removeItemAudited(id, reason, comments)}
            />)
        }
        else {
            _removeItemAudited(id, null, null);
        }
    }

    const _removeItemAudited = async (id, reason, comments) => {
        try {
            setModal(<ModalSpinner isShowing={true} hide={null} />);
            await removeOrderAPI(entryId, id, reason?.code, comments);
            setReload(true);
            setModal(null);
            Success("medicalRecordsNotifications.item_DeletedSuccessfully");
        }
        catch (error) {
            setModal(null);
            console.log(error);
            HandleApiError(error);
        }
    }
    //#endregion

    return (
        <>
            {modal}
            {
                showAddButton &&
                <div style={{ marginTop: '20px' }}>
                    <button key="buttonorder" name="orders" type="button"
                        className="btn btn-primary btn-fill btn-full"
                        onClick={handleAdd}
                        style={{ marginBottom: '5px', textAlign: 'left' }}>
                        <><IconSVG name="orders" fill="#fff" /> {t("commons.add")} {t(`medicalRecords.orders.title`)}</>
                    </button>
                </div>
            }
            <Orders
                withCard={false}
                entryId={entryId}
                data={orders}
                query={query}
                totalSize={totalSize}
                isLoading={isLoading}
                onTableChange={handleTableChange}
                onEdit={handleEdit}
                onAuditTrail={handleAuditTrail}
                onPrint={handlePrint}
                onCopy={handleCopy}
                onRecreateExternal={handleRecreateExternal}
                onSendEmail={handleSendEmail}
                onRemove={handleRemove}
            />
        </>
    );
}
OrdersContainer.defaultProps = {
    showAddButton: false
}
OrdersContainer.propTypes = {
    onSubmit: PropTypes.func,
    entryStatus: PropTypes.string,
    entryId: PropTypes.string,
    medicalRecordNumber: PropTypes.string,
    showAddButton: PropTypes.bool,
    expandedAll: PropTypes.bool
};

export const OrdersCreateContainer = (props) => {
    const { entryStatus, entryId, medicalRecordNumber, onSubmit } = props;
    const [, masterData, isLoading] = useDrug(medicalRecordNumber, entryId);
    const [, , , create] = useOrder(medicalRecordNumber);
    const [modal, setModal] = useState(null);

    const handleSubmit = async (data) => {

        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setModal(<CreateAudit
                onClose={() => setModal(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (reason) {
                data.auditReason = reason;
                data.auditComments = comments;
            }
            data.orders.forEach(x => { (!x.reasonId && x.reason && (x.reasonId = x.reason.id)); x.reason = null; }); // Mapeo Reason
            const response = await create(entryId, data);
            const createdOrder = response.data;
            props.onCreated(createdOrder);
        }
        catch (error) {
            console.log(error);
            HandleApiError(error);
            if (props.hide)
                props.hide()
        }
    }

    const handleCancel = () => {
        if (props.hide)
            props.hide()
    }

    if (!masterData || isLoading)
        return null;

    return (
        <>
            {
                modal ??
                <OrderContainerFormik
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    data={masterData.data}
                    events={masterData.events}
                    patient={props.patient}
                />
            }
        </>

    );
}

const OrderCopyContainer = (props) => {
    const { medicalRecordNumber, entryId, entryStatus, id, onSubmit, onCancel } = props;
    const [data, masterData, isLoading, create] = useOrder(medicalRecordNumber, entryId, id);
    const [modal, setModal] = useState(null);

    const handleSubmit = async (data) => {

        if (!data || !data.orders || data.orders.length === 0)
            return;

        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setModal(<CreateAuditModal
                onClose={() => setModal(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (data && data.orders && data.orders.length > 0) {
                if (reason) {
                    data.auditReason = reason;
                    data.auditComments = comments;
                }
                data.orders.forEach(x => { (!x.reasonId && x.reason && (x.reasonId = x.reason.id)); x.reason = null; }); // Mapeo Reason
                const response = await create(medicalRecordNumber, entryId, data);
                const createdOrder = response.data;
                props.onCreated(createdOrder);
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    if (!data || !masterData || isLoading)
        return null;

    return (
        <>
            {modal}
            <OrderContainerFormik
                onSubmit={handleSubmit}
                onCancel={onCancel}
                data={masterData.data}
                events={masterData.events}
                orders={data}
                patient={props.patient}
            />
        </>
    );
}

const OrderEditContainer = (props) => {
    const { medicalRecordNumber, entryId, entryStatus, id, onSubmit } = props;
    const [data, masterData, isLoading, , update] = useOrder(medicalRecordNumber, entryId, id);
    const [modal, setModal] = useState(null);

    const handleSubmit = async (data) => {

        if (!data || !data.orders || data.orders.length === 0)
            return;

        if (entryStatus === "Published" || entryStatus === "AutoPublished" || entryId != data.orders[0].entryId) {
            setModal(<CreateAuditModal
                onClose={() => setModal(null)}
                onSave={(reason, comments) => onConfirmSubmit(data, reason, comments)}
            />)
        }
        else {
            onConfirmSubmit(data);
        }
    }

    const onConfirmSubmit = async (data, reason, comments) => {
        try {
            if (data && data.orders && data.orders.length > 0) {
                if (reason) {
                    data.auditReason = reason;
                    data.auditComments = comments;
                }
                data.orders.forEach(x => { (!x.reasonId && x.reason && (x.reasonId = x.reason.id)); x.reason = null; }); // Mapeo Reason
                const response = await update(entryId, id, data)
                const createdOrder = response.data;
                props.onCreated(createdOrder);
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    if (!data || !masterData || isLoading)
        return null;

    return (
        <>
            {modal}
            <OrderContainerFormik
                onSubmit={handleSubmit}
                onCancel={props.onCancel}
                data={masterData.data}
                events={masterData.events}
                orders={data}
                patient={props.patient}
            />
        </>
    );
}

const OrderContainerFormik = withTranslation()(withFormik({
    mapPropsToValues: (props) => ({
        orderDate: props.orders?.orderDate ?? new Date(),
        medicalInsurance: props.orders?.medicalInsurance ?? props.patient?.medicalInsurance,
        medicalInsurancePlan: props.orders?.medicalInsurancePlan ?? props.patient?.medicalInsurancePlan,
        medicalInsuranceNumber: props.orders?.medicalInsuranceNumber ?? props.patient?.medicalInsuranceNumber,
        orders: props.orders?.orders ?? [],
        generalReason: props.orders?.generalReason ?? null,
    }),

    validationSchema: getMedicalRecordsSchema().orders,

    handleSubmit: (values, formikBag) => {
        formikBag.props.onSubmit(values);
    },

    displayName: 'OrdersForm',

})(OrdersForm));

OrderContainerFormik.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    data: PropTypes.object
};